export type MonthlyEventType = 'christmas' | 'sparks' | 'festival' | 'ancients' | 'valley';

const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

interface EventTypeMeta {
  title: string;
  description: string;
  previewImage?: string;
  previewEmoji?: string;
}

const EVENT_TYPE_META: Record<MonthlyEventType, EventTypeMeta> = {
  christmas: {
    title: 'Christmas Event',
    description: 'Complete daily tasks and light up Christmas snowhouses',
    previewImage: '/assets/event/christmas/title_2.png',
  },
  sparks: {
    title: 'Winter Sparks Event',
    description: 'Light fireworks each day and unlock winter wonders',
    previewImage: '/assets/event/sparks/title.png',
  },
  festival: {
    title: 'Festival Event',
    description: 'Complete daily challenges and unlock festival rewards',
    previewEmoji: '🎉',
  },
  ancients: {
    title: 'Ancients Event',
    description: 'Complete daily challenges and unlock ancient relic rewards',
    previewEmoji: '🏺',
  },
  valley: {
    title: 'Valley Event',
    description: 'Walk the spring valley and unlock trail rewards day by day',
    previewEmoji: '🌿',
  },
};

/**
 * Monthly event centralized config.
 *
 * Update only this file when a new month starts:
 * 1) add/update `monthToEventType`
 * 2) (optional) set `learnTestDefaultMonthOverride` for testing
 */
export const MONTHLY_EVENT_CONFIG = {
  // Fallback event when the current month is not configured yet
  fallbackEventType: 'festival' as MonthlyEventType,

  // Map month (YYYY-MM) to event component type
  monthToEventType: {
    '2025-12': 'christmas',
    '2026-01': 'sparks',
    '2026-02': 'festival',
    '2026-03': 'ancients',
    '2026-04': 'valley',
  } as Record<string, MonthlyEventType>,

  // Optional default month override for learn-test/local env
  // Example: '2026-03'
  learnTestDefaultMonthOverride: null as string | null,

  // Runtime override key in browser localStorage.
  // If set and valid, it has higher priority than learnTestDefaultMonthOverride.
  learnTestMonthOverrideStorageKey: 'monthly-event-test-month',

  // Keep current behavior: learn-test users can manually select event popup type
  enableLearnTestEventSelector: true,
};

export function isValidMonthlyEventMonth(month: string | null | undefined): month is string {
  return typeof month === 'string' && MONTH_PATTERN.test(month);
}

export function resolveMonthlyEventType(month: string): MonthlyEventType {
  return MONTHLY_EVENT_CONFIG.monthToEventType[month] ?? MONTHLY_EVENT_CONFIG.fallbackEventType;
}

export interface MonthlyEventSelectorOption {
  month: string;
  eventType: MonthlyEventType;
  title: string;
  description: string;
  previewImage?: string;
  previewEmoji?: string;
}

export function getMonthlyEventSelectorOptions(): MonthlyEventSelectorOption[] {
  return Object.entries(MONTHLY_EVENT_CONFIG.monthToEventType)
    .filter(([month]) => isValidMonthlyEventMonth(month))
    .sort(([monthA], [monthB]) => monthB.localeCompare(monthA))
    .map(([month, eventType]) => {
      const meta = EVENT_TYPE_META[eventType];
      return {
        month,
        eventType,
        title: `${month} · ${meta.title}`,
        description: meta.description,
        previewImage: meta.previewImage,
        previewEmoji: meta.previewEmoji,
      };
    });
}

export function resolveLearnTestMonthOverride(): string | null {
  const configuredMonth = MONTHLY_EVENT_CONFIG.learnTestDefaultMonthOverride;

  if (typeof window === 'undefined') {
    return isValidMonthlyEventMonth(configuredMonth) ? configuredMonth : null;
  }

  const runtimeOverride = window.localStorage.getItem(MONTHLY_EVENT_CONFIG.learnTestMonthOverrideStorageKey);
  if (isValidMonthlyEventMonth(runtimeOverride)) {
    return runtimeOverride;
  }

  return isValidMonthlyEventMonth(configuredMonth) ? configuredMonth : null;
}
